import styles from './Template.module.scss'
import ServicesFV from 'components/services/common/ServicesFV'
import Problem from 'components/services/common/Problem'
import AWSServiceLink from 'components/services/common/AWSServiceLink'
import Feature from 'components/services/common/Feature'
import Solution from 'components/services/common/Solution'
import type { SolutionItem } from 'components/services/common/Solution/Solution'
import Step from 'components/services/common/Step'
import type { StepItem } from 'components/services/common/Step/Step'
import Faq from 'components/services/common/Faq'
import { cloudnativeFaqList } from 'components/common/schema/data/faqs/cloudnative'
import RelatedPosts from 'components/services/common/RelatedPosts'
import type { RelatedPostsItem } from 'components/services/common/RelatedPosts/RelatedPosts'
import FooterCTA from 'components/common/FooterCTA'

const paths = [
  { name: 'TOP', url: '/' },
  { name: 'Services', url: '#' },
  { name: 'クラウドネイティブ', url: '#' },
]

const ServicesFVList = {
  category: 'モダンアプリケーション開発',
  title: (
    <>
      <span>ク</span>
      <span>ラ</span>
      <span>ウ</span>
      <span>ド</span>
      <span>ネ</span>
      <span>イ</span>
      <span>テ</span>
      <span>ィ</span>
      <span>ブ</span>
    </>
  ),
  text: 'クラウドリフト後の次の一手として、サーバーレスとデータ統合基盤を活用し、真のクラウドネイティブを実現します。包括的なアーキテクチャ設計からDevOpsの導入、継続的な運用支援までを一貫して提供し、ステップ導入によって無理なくクラウドネイティブへの移行を支援します。',
  imgSrc: '/img/services/services_fv_cloudnative.webp',
  alt: 'クラウドネイティブ',
}

const ProblemList = {
  titleProblem: 'クラウドリフト後の課題',
  problem1: 'IaaSメインで稼動しており、インフラ運用コストや管理工数がまだ多い',
  problem2: '本来のクラウドメリット（自動スケーリング、サーバーレスなど）を活かしきれていない',
  problem3: '部門ごとにデータが分散し、活用に時間やコストがかかる',
  problem4: 'BI・機械学習を導入したいが基盤が整っていない',
  problem5: '',

  titleCanDo: 'Aitriaができること',
  CanDo1: 'IaaS上にある既存アプリを段階的にサーバーレス・コンテナへ移行し、アプリケーションアーキテクチャを最適化',
  CanDo2: 'DevOpsの導入によりリリースサイクルを加速',
  CanDo3: '部門やサービスごとに分断されたデータを一元管理するプラットフォームを構築',
  CanDo4: 'リアルタイム分析や機械学習に耐えうるデータパイプラインの整備',
  CanDo5: '',
}

const featureList = [
  {
    imgSrc: '/img/services/icon_knowledge.svg',
    title: '包括的なアーキテクチャ設計',
    item1: 'インフラ〜アプリケーション〜データ基盤まで、一気通貫で最適な設計を提案',
    item2: '全体を見渡したプランニングで、無駄な再設計を抑制',
    item3: '長期的な拡張性を考慮した設計アプローチ',
  },
  {
    imgSrc: '/img/services/icon_innovative.svg',
    title: 'DevOpsの導入による開発プロセスの効率化',
    item1: 'CI/CDパイプラインの整備で高速リリース',
    item2: '運用チームと開発チームの連携を促進し、保守コストを削減',
    item3: '自動テスト・デプロイによる品質向上と開発サイクルの短縮',
  },
  {
    imgSrc: '/img/services/icon_onestop.svg',
    title: '最新技術と豊富な実績に基づく運用支援',
    item1: 'サーバーレス、マイクロサービス、IaC、オブザーバビリティツール、データレイク/ウェアハウスなど',
    item2: '豊富な実績ノウハウを組み合わせ、最適化・リスク軽減を実現',
    item3: 'クラウドプロバイダー認定資格保有者による技術サポート',
  },
]

const solutionList: SolutionItem = {
  sectionTitle: 'ソリューション',
  sectionRead: 'クラウドネイティブ化とデータ統合基盤の構築を通じて、ビジネスの俊敏性と競争力を高めます',
  solutions: [
    {
      title: 'クラウドネイティブ化支援',
      subTitle1: 'サーバーレスアーキテクチャ導入',
      text1:
        'AWS Lambda、Azure Functions、Google Cloud Functionsなどのサーバーレスサービスを活用し、イベント駆動型アーキテクチャで自動スケーリングを実現。従量課金モデルによりコストと運用負荷を大幅に削減し、ビジネス開発に集中できる環境を提供します。',
      subTitle2: 'マイクロサービス化',
      text2:
        'モノリシックなシステムから段階的にマイクロサービス化を実現。Docker、Kubernetesなどのコンテナ技術を活用し、各サービスの独立したデプロイとスケーリングを可能にします。開発チーム単位での自律性向上を促進するとともに、サービス間通信の最適化と効率的なAPI設計で連携を強化します。',
      subTitle3: 'IaC/CI/CDの導入',
      text3:
        'Terraformや CloudFormation を活用したインフラのコード化により、環境構築を自動化します。GitlabやGitHub Actions などの CI/CD ツールと連携し、テストからデプロイまでのパイプラインを構築。これにより開発・ステージング・本番環境の一貫性と再現性を確保し、安定したシステム運用を実現します。',
      subTitle4: 'オブザーバビリティ設計',
      text4:
        'ログ・メトリクス・トレースをDatadog, New Relic などの可視化ツールで一元管理します。リアルタイムモニタリングにより障害の早期発見と迅速な対応を実現し、システムの安定運用を確保。また、パフォーマンスのボトルネックを特定・分析し、継続的な最適化を支援します。',
      imgSrc1: '/img/services/cloudnative_logo01_01.svg',
      imgSrc1Width: '90',
      imgSrc1Height: '46',
      alt1: 'AWS Lambda',
      imgSrc2: '/img/services/cloudnative_logo01_02.svg',
      imgSrc2Width: '90',
      imgSrc2Height: '46',
      alt2: 'Azure Functions',
      imgSrc3: '/img/services/cloudnative_logo01_03.svg',
      imgSrc3Width: '90',
      imgSrc3Height: '46',
      alt3: 'AWS API Gateway',
      imgSrc4: '/img/services/cloudnative_logo01_04.svg',
      imgSrc4Width: '90',
      imgSrc4Height: '46',
      alt4: 'Azure API Management',
      imgSrc5: '/img/services/cloudnative_logo01_05.svg',
      imgSrc5Width: '114',
      imgSrc5Height: '29',
      alt5: 'docker',
      imgSrc6: '/img/services/cloudnative_logo01_06.svg',
      imgSrc6Width: '90',
      imgSrc6Height: '46',
      alt6: 'new relic',
      imgSrc7: '/img/services/cloudnative_logo01_07.svg',
      imgSrc7Width: '141',
      imgSrc7Height: '37',
      alt7: 'Terraform',
      imgSrc8: '/img/services/cloudnative_logo01_08.svg',
      imgSrc8Width: '135',
      imgSrc8Height: '24',
      alt8: 'kubernetes',
    },
    {
      title: 'データ統合基盤・分析基盤構築',
      subTitle1: 'データレイク／DWHの設計',
      text1:
        'S3、ADLS、BigQueryなどのクラウドサービスでバッチとストリーミング処理を統合管理。大規模データに対応するスケーラブルなストレージアーキテクチャを設計し、適切なデータガバナンスとセキュリティ対策により、データ資産の保護と有効活用を実現します。',
      subTitle2: 'データパイプライン構築',
      text2:
        'ETL/ELT処理をGlue、Dataflow、Data Factoryで効率化。リアルタイム処理や機械学習基盤と柔軟に連携し、ビジネス要件に適したデータパイプラインを構築。データ品質管理と異常検知の仕組みにより、高品質なデータ活用基盤を提供します。',
      subTitle3: 'BI／分析環境の整備',
      text3:
        'Tableau、Looker、Power BIなどのツールと連携し、分析結果をダッシュボードで視覚化。経営層から実務担当者まで、各階層が意思決定に活用できるセルフサービス分析環境を構築し、データドリブンな組織文化の醸成を支援します。',
      subTitle4: '',
      text4: '',
      imgSrc1: '/img/services/cloudnative_logo02_01.svg',
      imgSrc1Width: '50',
      imgSrc1Height: '50',
      alt1: 'Amazon S3',
      imgSrc2: '/img/services/cloudnative_logo02_02.svg',
      imgSrc2Width: '50',
      imgSrc2Height: '50',
      alt2: 'BigQuery',
      imgSrc3: '/img/services/cloudnative_logo02_03.svg',
      imgSrc3Width: '49',
      imgSrc3Height: '49',
      alt3: 'Glue',
      imgSrc4: '/img/services/cloudnative_logo02_04.svg',
      imgSrc4Width: '58',
      imgSrc4Height: '58',
      alt4: 'Dataflow',
      imgSrc5: '/img/services/cloudnative_logo02_05.svg',
      imgSrc5Width: '56',
      imgSrc5Height: '56',
      alt5: 'Power BI',
      imgSrc6: '/img/services/cloudnative_logo02_06.svg',
      imgSrc6Width: '60',
      imgSrc6Height: '60',
      alt6: 'Looker',
      imgSrc7: '/img/services/cloudnative_logo02_07.svg',
      imgSrc7Width: '170',
      imgSrc7Height: '46',
      alt7: 'Terraform',
      isImg7: true,
      imgSrc8: '',
      imgSrc8Width: '',
      imgSrc8Height: '',
      alt8: '',
    },
  ],
}

const stepList: StepItem = {
  sectionTitle: '導入ステップ',
  sectionRead: '一挙に全てを変えるのではなく、ステップを踏みながらクラウドネイティブ化を進めます',
  step: [
    {
      no: '01.',
      title: 'リプラットフォーム',
      text: '運用負荷を軽減するため、既存ワークロードを EKS/ECS や RDS などのマネージドサービスへ載せ替え、マネージド PaaS への移行を実現',
    },
    {
      no: '02.',
      title: 'コンテナ化・サーバレス化',
      text: 'スケール弾性とデプロイ高速化を獲得するため、既存ワークロードをコンテナ（Docker/Kubernetes）や AWS Lambda などに移行し、クラウドネイティブなスケーラビリティと高速リリースサイクルを実現',
    },
    {
      no: '03.',
      title: 'リファクタリング／ドメインモダナイゼーション',
      text: 'アプリとデータをクラウドネイティブ設計へ刷新し、モノリシック構造をドメイン単位に分割、APIファースト設計とイベント駆動アーキテクチャでクラウドネイティブ化の推進',
    },
    {
      no: '04.',
      title: 'CI/CD・DevSecOps 自動化',
      text: '変更速度と品質を両立するため、CI/CD パイプラインを整備し、テスト・セキュリティチェック・Infrastructure as Code を自動化して継続的デリバリーを実現',
    },
    {
      no: '05.',
      title: '運用・監視・最適化',
      text: 'SRE と FinOps を導入し、SLO 運用・コスト最適化・ポストモーテム文化の醸成を通じて継続的に改善し、ROI を最大化する体制を構築',
    },
  ],
}

const relatedPostsList: RelatedPostsItem = {
  sectionTitle: 'クラウドネイティブの関連記事',
  relatedPosts: [
    {
      title: 'クラウドネイティブとは？ビジネスの成長を加速させる技術を徹底解説！',
      link: 'https://aitria.co.jp/in-pocket/what-is-cloudnative/',
      thumbnail: 'https://aitria.co.jp/in-pocket/wp-content/uploads/2024/09/cloudnative.png',
      day: '2025年5月1日',
      category: '開発・エンジニアリング',
    },
    {
      title: 'AI時代のクラウドネイティブ開発！ TDD・BDDなど4つの手法×AI活用を徹底解説',
      link: 'https://aitria.co.jp/in-pocket/ai-cloudnative/',
      thumbnail: 'https://aitria.co.jp/in-pocket/wp-content/uploads/2025/08/ai-cloudnative.png',
      day: '2025年10月9日',
      category: '開発・エンジニアリング',
    },
    {
      title: '【初心者向け】クラウドリフト実践！計画から移行、運用まで徹底解説',
      link: 'https://aitria.co.jp/in-pocket/cloud-lift/',
      thumbnail: 'https://aitria.co.jp/in-pocket/wp-content/uploads/2025/03/CloudLift.png',
      day: '2025年9月28日',
      category: '開発・エンジニアリング',
    },
    {
      title: 'ハイブリッドクラウドとは？マルチクラウドとの違いやメリット、導入ステップまで解説',
      link: 'https://aitria.co.jp/in-pocket/what-is-hybrid-cloud/',
      thumbnail: 'https://aitria.co.jp/in-pocket/wp-content/uploads/2025/03/hybrid_cloud-1.png',
      day: '2025年10月9日',
      category: '開発・エンジニアリング',
    },
  ],
}

const CloudNative: React.FC = () => {
  return (
    <>
      <div className={styles.space}>
        <ServicesFV ServicesFVEach={ServicesFVList} paths={paths} />
        <Problem ProblemEach={ProblemList} />
        <AWSServiceLink />
      </div>
      <div className={styles.backGround}>
        <Feature FeatureEach={featureList} />
      </div>
      <Solution SolutionEach={[solutionList]} />
      <div className={styles.backGround}>
        <Step StepEach={[stepList]} />
      </div>
      <Faq FaqEach={[cloudnativeFaqList]} />
      <div className={styles.backGroundGray}>
        <RelatedPosts RelatedPostsEach={[relatedPostsList]} />
      </div>
      <FooterCTA />
    </>
  )
}

export default CloudNative
